// ✅ Create a NEW component: ExpiryItemsCard.jsx
// Put it in: src/.../Dashboard/ExpiryItemsCard.jsx (same folder as Dashboard.jsx is fine)

import React, { useEffect, useMemo, useState } from 'react';
import ApiService from '../../../services/apiService';

// ---------- helpers ----------
const numberFmt = (n, digits = 0) =>
  new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(Number.isFinite(+n) ? +n : 0);

const isISODateLike = (v) => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}(T.*)?$/.test(v) && !Number.isNaN(Date.parse(v));

const toYMD = (d) => new Date(d).toISOString().slice(0, 10);

const normalizeCell = (key, val) => {
  if (val === null || val === undefined) return '—';
  if (key === 'BatchNo') {
    if (typeof val === 'object') return Object.keys(val || {}).length ? JSON.stringify(val) : '—';
    return String(val).trim() || '—';
  }
  if (typeof val === 'object') {
    try {
      return JSON.stringify(val);
    } catch {
      return String(val);
    }
  }
  return val;
};

const formatCell = (key, val) => {
  if (val === '—') return val;

  // ✅ BatchNo no commas
  if (key === 'BatchNo') return String(val);

  // Date formatting
  if (isISODateLike(val)) {
    const d = new Date(val);
    return new Intl.DateTimeFormat('en-GB', { year: 'numeric', month: 'short', day: '2-digit' }).format(d);
  }

  // Numeric formatting
  if (Number.isFinite(+val) && String(val).trim() !== '') {
    const asNum = +val;
    const digits = Number.isInteger(asNum) ? 0 : 2;
    return numberFmt(asNum, digits);
  }

  return String(val);
};

const daysUntil = (isoDate) => {
  if (!isISODateLike(isoDate)) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(isoDate);
  d.setHours(0, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
};

const pick = (row, keys, fallback = '—') => {
  for (const k of keys) {
    if (row?.[k] !== undefined) return row[k];
  }
  return fallback;
};

// ---------- component ----------
const ExpiryItemsCard = ({ pageSize = 8, defaultDays = 10 }) => {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(0);

  const [days, setDays] = useState(defaultDays);
  const [batchNo, setBatchNo] = useState('');
  const [search, setSearch] = useState('');

  const [rows, setRows] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  // fetch branches (optional but improves UX)
  const fetchBranches = async () => {
    try {
      const apiService = new ApiService();
      const res = await apiService.get('GET_BRANCH');
      const list = res?.data?.result || [];
      setBranches(list);
    } catch (e) {
      // silent fail, dashboard should still work
      console.error('Failed to fetch branches', e);
    }
  };

  const fetchExpiry = async () => {
    setLoading(true);
    setErr('');
    try {
      const apiService = new ApiService();
      const params = new URLSearchParams();

      const now = new Date();
      const from = new Date(now);
      from.setDate(now.getDate() - Number(days || defaultDays));

      params.append('PageNo', 1);
      params.append('PageSize', pageSize);

      params.append('ReportType', 'Expiry Report');
      params.append('Measure', 'Revenue');
      params.append('StartDate', toYMD(from));
      params.append('EndDate', toYMD(now));

      const batch = String(batchNo || '').trim();
      params.append('BatchNo', batch ? batch : 'null');

      params.append('Comparison', 'No Comparison');
      params.append('BranchId', Number(branchId || 0));

      const res = await apiService.getApiParamater('GET_SALE_SUMMERY', `?${params.toString()}`);
      const result = Array.isArray(res?.data?.result) ? res.data.result : [];

      const normalized = result.map((obj) => {
        const out = {};
        Object.keys(obj || {}).forEach((k) => (out[k] = normalizeCell(k, obj[k])));
        return out;
      });

      setRows(normalized);
      setLastUpdated(new Date());
    } catch (e) {
      console.error(e);
      setErr('Failed to load expiry items.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
    fetchExpiry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // refetch when filters change (debounced-ish via button only is also ok; here we refetch on click only)
  const onApply = () => fetchExpiry();

  // column model (professional: fixed, predictable columns)
  const columns = useMemo(
    () => [
      { key: 'Item', label: 'Item' },
      { key: 'BatchNo', label: 'Batch No' },
      { key: 'ExpiryDate', label: 'Expiry' },
      { key: 'Qty', label: 'Qty' },
      { key: 'Branch', label: 'Branch' },
      { key: 'Status', label: 'Status' },
    ],
    []
  );

  const viewRows = useMemo(() => {
    const mapped = rows.map((r) => {
      const item = pick(r, ['ItemName', 'ProductName', 'Item', 'Product', 'Name']);
      const batch = pick(r, ['BatchNo']);
      const exp = pick(r, ['ExpiryDate', 'Expiry', 'ExpDate', 'ExpireDate']);
      const qty = pick(r, ['Qty', 'Quantity', 'StockQty', 'BalanceQty']);
      const branch = pick(r, ['BranchName', 'Branch']);

      const d = daysUntil(exp);
      let status = { text: '—', cls: 'badge bg-secondary-subtle text-secondary' };
      if (typeof d === 'number') {
        if (d < 0) status = { text: `Expired (${Math.abs(d)}d)`, cls: 'badge bg-danger-subtle text-danger' };
        else if (d <= 7) status = { text: `Urgent (${d}d)`, cls: 'badge bg-danger-subtle text-danger' };
        else if (d <= 30) status = { text: `Soon (${d}d)`, cls: 'badge bg-warning-subtle text-warning' };
        else status = { text: `OK (${d}d)`, cls: 'badge bg-success-subtle text-success' };
      }

      return { _raw: r, Item: item, BatchNo: batch, ExpiryDate: exp, Qty: qty, Branch: branch, Status: status };
    });

    if (!search.trim()) return mapped;

    const q = search.trim().toLowerCase();
    return mapped.filter((x) =>
      [x.Item, x.BatchNo, x.Branch, x.ExpiryDate, x.Qty].some((v) =>
        String(v ?? '')
          .toLowerCase()
          .includes(q)
      )
    );
  }, [rows, search]);

  return (
    <div className="dashcard p-3 mt-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
        <div>
          <div className="chatrtHead">Expiry Items</div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Quick view for expiring stock • {lastUpdated ? `Updated: ${lastUpdated.toLocaleString()}` : 'Not updated yet'}
          </div>
        </div>

        <div className="d-flex gap-2 align-items-center flex-wrap">
          <button className="btn btn-outline-secondary btn-sm" onClick={fetchExpiry} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Filters (compact, dashboard friendly) */}
      <div className="row g-2 mt-2 align-items-end">
        {/* <div className="col-12 col-md-3">
          <label className="form-label mb-1" style={{ fontSize: 12 }}>
            Range
          </label>
          <select className="form-select form-select-sm" value={days} onChange={(e) => setDays(e.target.value)}>
            <option value={7}>Last 7 days</option>
            <option value={10}>Last 10 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div> */}

        <div className="col-12 col-md-3">
          <label className="form-label mb-1" style={{ fontSize: 12 }}>
            Branch
          </label>
          <select className="form-select form-select-sm" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            <option value={0}>All branches</option>
            {branches.map((b) => (
              <option key={b?.id} value={b?.id}>
                {b?.branchName}
              </option>
            ))}
          </select>
        </div>

        <div className="col-12 col-md-3">
          <label className="form-label mb-1" style={{ fontSize: 12 }}>
            Batch No
          </label>
          <input className="form-control form-control-sm" placeholder="Optional" value={batchNo} onChange={(e) => setBatchNo(e.target.value)} />
        </div>

        <div className="col-12 col-md-3 d-flex gap-2">
          <div className="flex-grow-1">
            <label className="form-label mb-1" style={{ fontSize: 12 }}>
              Search
            </label>
            <input className="form-control form-control-sm" placeholder="Item / Batch / Branch" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-primary btn-sm" onClick={onApply} disabled={loading} style={{ height: 31, marginTop: 22 }}>
            Apply
          </button>
        </div>
      </div>

      {err ? <div className="alert alert-danger mt-3 mb-0">{err}</div> : null}

      {/* Table */}
      <div className="custom-table-container mt-3" style={{ overflowX: 'auto' }}>
        <table className="custom-table">
          <thead>
            <tr>
              {columns.map((c) => (
                <th key={c.key} style={{ whiteSpace: 'nowrap', position: 'sticky', top: 0, zIndex: 1 }}>
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {!loading && viewRows.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="text-center p-4">
                  <div className="text-muted">No expiry items found for selected filters.</div>
                </td>
              </tr>
            )}

            {loading &&
              Array.from({ length: pageSize }).map((_, i) => (
                <tr key={i}>
                  {columns.map((c) => (
                    <td key={c.key} style={{ whiteSpace: 'nowrap' }}>
                      <span className="placeholder col-8" />
                    </td>
                  ))}
                </tr>
              ))}

            {!loading &&
              viewRows.map((r, idx) => (
                <tr key={idx} style={{ cursor: 'default' }}>
                  <td style={{ maxWidth: 260 }}>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={String(r.Item)}>
                      {formatCell('Item', r.Item)}
                    </div>
                  </td>

                  <td style={{ whiteSpace: 'nowrap' }}>{formatCell('BatchNo', r.BatchNo)}</td>

                  <td style={{ whiteSpace: 'nowrap' }}>{formatCell('ExpiryDate', r.ExpiryDate)}</td>

                  <td style={{ whiteSpace: 'nowrap' }}>{formatCell('Qty', r.Qty)}</td>

                  <td style={{ maxWidth: 180 }}>
                    <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={String(r.Branch)}>
                      {formatCell('Branch', r.Branch)}
                    </div>
                  </td>

                  <td style={{ whiteSpace: 'nowrap' }}>{r.Status?.text !== '—' ? <span className={r.Status.cls}>{r.Status.text}</span> : '—'}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
        <div className="text-muted" style={{ fontSize: 12 }}>
          Showing up to <b>{pageSize}</b> records (dashboard preview)
        </div>
      </div>
    </div>
  );
};

export default ExpiryItemsCard;
